import { el, VNode } from "@dhx/ts-common/dom";
import { IBaseGroupConfig, IBaseCoords, BaseDirection, ShapeConfig } from "@dhx/ts-diagram";

export function getCircleTpl(config: ShapeConfig, index?: number): VNode {
	if ((!config.$count && config.open !== false) || !config.$connection) return null;

	const slim = config.dir === "vertical";
	let hide = config.open === false;

	const hW = config.width / 2;
	const hH = config.height / 2;

	const coords = {
		x: slim ? 0 : hW,
		y: slim ? hH : config.height,
	};

	let css = "";

	if (Array.isArray(config.dir)) {
		if (config.dir[index] === "verticalLeft") {
			coords.x = 0;
			coords.y = hH;
			css += "icon-left ";
			hide = config.openDir.left === false;
		}
		if (config.dir[index] === "verticalRight") {
			coords.x = config.width;
			coords.y = hH;
			css += "icon-right ";
			hide = config.openDir.right === false;
		}
	} else {
		if (config.dir === "verticalLeft") {
			coords.x = 0;
			coords.y = hH;
		}
		if (config.dir === "verticalRight") {
			coords.x = config.width;
			coords.y = hH;
		}
	}

	return el(
		"div",
		{
			class: hide ? "dhx_expand_icon " : "dhx_hide_icon ",
			style: {
				position: "absolute",
				top: coords.y,
				left: coords.x,
			},
		},
		[
			el(
				"div.dhx_diagram__icon",
				{
					style: {
						background: config.$expandColor,
					},
				},
				[
					el("i.dxi", {
						class: `${hide ? "dxi-plus " : "dxi-minus "}${css}`,
					}),
				]
			),
		]
	);
}

export function getHeaderTpl(config: ShapeConfig): VNode {
	const { width } = config;
	const height = 4;
	const color = config.headerColor || "#20b6e2";
	return el("div", {
		class: "dhx_item_header",
		style: {
			height,
			width,
			top: 0,
			left: 0,
			position: "absolute",
			background: color,
		},
	});
}

export function getTextTemplate(config: ShapeConfig, content: any): VNode {
	const { width, height } = config;
	if (typeof config.text === "string" || typeof config.title === "string") {
		return el(
			"div.shape_content-container",
			{
				style: {
					width,
					height,
					top: 0,
					left: 0,
					overflow: "hidden",
					transform: "translate(0 0)",
					position: "absolute",
				},
			},
			[
				el(
					"div",
					{
						class: "shape_content",
						style: {
							width: config.width,
							height: config.height,
							wordBreak: "break-word",
							whiteSpace: "pre-wrap",
							overflow: "hidden",
						},
					},
					[].concat(content)
				),
			]
		);
	}
	return null;
}

export function getShapeCss(config: ShapeConfig): { [key: string]: string | number } {
	const verticalAlign = {
		bottom: "flex-end",
		top: "flex-start",
		center: "center",
	};
	return {
		width: config.width,
		height: config.height,
		display: "flex",
		color: config.fontColor,
		"flex-direction": "column",
		"justify-content": verticalAlign[config.textVerticalAlign],
		"text-align": config.textAlign,
		"line-height": config.lineHeight,
		"font-size": config.fontSize,
		"font-style": config.fontStyle || "normal",
		"font-weight": config.fontWeight || "normal",
		"word-break": "break-word",
		"white-space": "pre-wrap",
	};
}

interface IExpandIconConfig {
	open: boolean;
	color: string;
	position: BaseDirection;
}

export function getExpandIcon(config: IExpandIconConfig): VNode {
	const { open, color, position } = config;
	let up;
	let down;
	switch (position) {
		case "top":
			up = "dxi-chevron-up";
			down = "dxi-chevron-down";
			break;
		case "bottom":
			up = "dxi-chevron-down";
			down = "dxi-chevron-up";
			break;
		case "right":
			up = "dxi-chevron-right";
			down = "dxi-chevron-left";
			break;
		case "left":
			up = "dxi-chevron-left";
			down = "dxi-chevron-right";
			break;
	}
	const currentClass = open ? `dxi ${down} dhx_hide_icon` : `dxi ${up} dhx_expand_icon`;

	return el("div.dhx_diagram_icon", [
		el("i", {
			class: currentClass,
			style: {
				color,
			},
		}),
	]);
}

interface IMenuIconConfig {
	dir: "rows" | "cols";
	color: string;
}

export function getMenuIcon(config: IMenuIconConfig): VNode {
	const { dir, color } = config;
	const currentClass = dir === "cols" ? "dxi dxi-dots-vertical" : "dxi dxi-dots-horizontal";

	return el("div.dhx_diagram_icon.dhx_diagram_menu_icon", [
		el("i", {
			class: currentClass,
			style: {
				color,
			},
		}),
	]);
}

export function getGroupContainerStyle(
	config: IBaseGroupConfig,
	coords: IBaseCoords
): { [key: string]: string | number } {
	const { $width, $height, $captureArea, $group, $selected, type } = config;
	const { borderStyle, strokeWidth, stroke, overFill, partiallyFill, fill } = config.style;
	const zIndex = $group || $selected ? ($selected && type !== ("$sgroup" || "$swimlane") ? 2 : 1) : 0;

	let background;
	switch ($captureArea) {
		case "over":
			background = overFill;
			break;
		case "partially":
			background = partiallyFill;
			break;
		default:
			background = fill;
			break;
	}

	return {
		position: "absolute",
		background,
		outline: `${strokeWidth}px ${borderStyle} ${stroke}`,
		outlineOffset: -strokeWidth,
		width: $width,
		height: $height,
		top: coords.y,
		left: coords.x,
		zIndex,
	};
}

export function getSwimlaneBackGround(
	config: IBaseGroupConfig,
	coords: IBaseCoords
): { [key: string]: string | number } {
	const { $width, $height, $captureArea } = config;
	const { overFill, partiallyFill, fill } = config.style;

	let background;
	switch ($captureArea) {
		case "over":
			background = overFill;
			break;
		case "partially":
			background = partiallyFill;
			break;
		default:
			background = fill;
			break;
	}

	return {
		position: "absolute",
		background,
		width: $width,
		height: $height,
		top: coords.y,
		left: coords.x,
		zIndex: -1,
	};
}

export function getSwimlaneContainerStyle(
	config: IBaseGroupConfig,
	coords: IBaseCoords
): { [key: string]: string | number } {
	const { $width, $height, $group, $selected, type } = config;
	const { borderStyle, strokeWidth, stroke } = config.style;
	const zIndex = $group || $selected ? ($selected && type !== ("$sgroup" || "$swimlane") ? 2 : 1) : 0;

	return {
		position: "absolute",
		outline: `${strokeWidth}px ${borderStyle} ${stroke}`,
		outlineOffset: -strokeWidth,
		width: $width,
		height: $height,
		top: coords.y,
		left: coords.x,
		zIndex,
	};
}

export const getTextStyle = config => {
	const TEXT_PADDING = 4;
	const MENU_OFFSET = 20;

	const {
		lineHeight,
		textAlign,
		textVerticalAlign,
		fontSize,
		fontColor,
		fontStyle,
		fontWeight,
		container: { width, height },
	} = config;

	const align = {
		center: "center",
		left: "flex-start",
		right: "flex-end",
		bottom: "flex-end",
		top: "flex-start",
	};

	return {
		"justify-content": align[textAlign],
		"align-items": align[textVerticalAlign],
		fontStyle: fontStyle || "normal",
		fontWeight: fontWeight || "500",
		lineHeight: `${parseInt(lineHeight)}px`,
		fontSize,
		color: fontColor,
		width: typeof width === "number" ? `${width - TEXT_PADDING - MENU_OFFSET}px` : width,
		height: typeof height === "number" ? `${height - TEXT_PADDING - MENU_OFFSET}px` : height,
	};
};
